<template>
  <div>
    <div class="d-flex align-center mb-2">
      <v-icon small color="green darken-1" class="mr-1">mdi-whatsapp</v-icon>
      <span class="font-weight-bold">Hallazgos Twilio IA</span>
      <span v-if="hallazgos" class="text-caption grey--text ml-2">{{ textoPeriodoHallazgos( hallazgos ) }}</span>
      <v-spacer></v-spacer>
      <v-btn
        v-if="hallazgos && hallazgos.estado === 'listo' && hallazgos.conversaciones"
        color="green darken-1"
        x-small
        tile
        outlined
        @click="verConversaciones()"
      >
        <v-icon x-small left>mdi-format-list-checks</v-icon>
        Ver conversaciones
      </v-btn>
    </div>

    <div v-if="!hallazgos" class="grey--text">
      Aún no se han analizado sus conversaciones de WhatsApp.
    </div>
    <div v-else-if="hallazgos.estado === 'procesando'" class="blue--text">
      Analizando conversaciones, transcribiendo audios y revisando con IA. Puede tardar un par de minutos.
    </div>
    <div v-else-if="hallazgos.estado === 'error'" class="red--text">
      {{ hallazgos.error }}
    </div>

    <template v-else>
      <div class="mb-3">
        <v-chip small class="mr-2 mb-1" label>{{ hallazgos.conversaciones }} conversaciones</v-chip>
        <v-chip small class="mr-2 mb-1" label>Respuesta promedio: {{ formatoMinutosRetro( hallazgos.respuesta_promedio_min ) }}</v-chip>
        <v-chip small class="mr-2 mb-1" label>Primera respuesta: {{ formatoMinutosRetro( hallazgos.primera_respuesta_promedio_min ) }}</v-chip>
        <v-chip small class="mr-2 mb-1" label>Audios: {{ hallazgos.pct_audios !== null ? hallazgos.pct_audios + '%' : '—' }}</v-chip>
        <v-chip small class="mr-2 mb-1" label>Sin responder: {{ hallazgos.sin_responder }}</v-chip>
        <v-chip small class="mr-2 mb-1" label>Masivos: {{ hallazgos.mensajes_repetidos }}</v-chip>
        <template v-if="tieneSeguimiento">
          <v-chip small class="mr-2 mb-1" label color="orange lighten-5" title="El prospecto nunca escribió después del primer mensaje del personal">
            No contestaron el 1er mensaje: {{ conPorcentaje( hallazgos.no_contestaron_primer ) }}
          </v-chip>
          <v-chip small class="mr-2 mb-1" label color="blue lighten-5">
            Con PDF: {{ conPorcentaje( hallazgos.contactos_pdf ) }}
          </v-chip>
          <v-chip small class="mr-2 mb-1" label color="red lighten-5" title="El prospecto no contestó y pasaron más de 4 h hábiles sin enviarle otro mensaje">
            Sin segunda vuelta: {{ conPorcentaje( hallazgos.sin_segunda_vuelta ) }}
          </v-chip>
        </template>
        <template v-if="hallazgos.conversaciones_ia">
          <v-chip small class="mr-2 mb-1" label color="deep-purple lighten-5">
            Revisadas con IA: {{ hallazgos.conversaciones_ia }}
          </v-chip>
          <v-chip small class="mr-2 mb-1" label color="deep-purple lighten-5">
            Necesidades {{ hallazgos.necesidades }}/{{ hallazgos.conversaciones_ia }}
          </v-chip>
          <v-chip small class="mr-2 mb-1" label color="deep-purple lighten-5">
            Propuesta de valor {{ hallazgos.propuesta_valor }}/{{ hallazgos.conversaciones_ia }}
          </v-chip>
          <v-chip small class="mr-2 mb-1" label color="deep-purple lighten-5">
            Siguiente paso {{ hallazgos.siguiente_paso }}/{{ hallazgos.conversaciones_ia }}
          </v-chip>
          <v-chip small class="mr-2 mb-1" label color="deep-purple lighten-5">
            Objeciones respondidas {{ hallazgos.objeciones_respondidas }}/{{ hallazgos.objeciones }}
          </v-chip>
        </template>
      </div>

      <div v-for="(texto, i) in hallazgos.hallazgos" :key="'h-'+i" class="d-flex mb-1">
        <v-icon small color="green darken-1" class="mr-2">mdi-magnify-scan</v-icon>
        <span>{{ texto }}</span>
      </div>

      <v-row v-if="listaHallazgos( 'fortalezas' ).length || listaHallazgos( 'areas_oportunidad' ).length" class="mt-1">
        <v-col cols="12" md="6">
          <ListaPuntosIa :items="listaHallazgos( 'fortalezas' )" tipo="fortaleza" titulo="Fortalezas" />
        </v-col>
        <v-col cols="12" md="6">
          <ListaPuntosIa :items="listaHallazgos( 'areas_oportunidad' )" tipo="area" titulo="Áreas de oportunidad" />
        </v-col>
      </v-row>

      <v-alert v-if="hallazgos.mensaje" dense text color="deep-purple" class="mt-3 mb-0">
        {{ hallazgos.mensaje }}
      </v-alert>

      <div class="text-caption grey--text mt-2">
        Generado el {{ String(hallazgos.fecha_generacion).replace('T', ' ').slice(0, 16) }}.
        Tiempo de respuesta en horario laboral (lunes a sábado de 9:00 a 20:00).
        <span v-if="!tieneSeguimiento">Regenera el análisis para ver primer mensaje sin respuesta, PDF, segunda vuelta, fortalezas y áreas de oportunidad.</span>
      </div>
    </template>

    <v-dialog v-model="dialogConversaciones" max-width="1300px" scrollable>
      <v-card>
        <v-card-title>
          <span class="text-subtitle-1">Conversaciones de {{ nombre }}</span>
          <v-spacer></v-spacer>
          <v-switch
            v-model="soloAnalizadasIa"
            label="Solo revisadas con IA"
            hide-details
            dense
            class="mt-0 mr-4"
          ></v-switch>
          <v-btn icon small @click="dialogConversaciones = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <v-data-table
            :headers="headersTablaConversaciones"
            :items="conversacionesFiltradas"
            :loading="cargandoConversaciones"
            item-key="id"
            class="elevation-0"
            :mobile-breakpoint="100"
            dense
          >
            <template v-slot:item.primera_respuesta_min="{ item }">{{ formatoMinutosRetro( item.primera_respuesta_min ) }}</template>
            <template v-slot:item.respuesta_promedio_min="{ item }">{{ formatoMinutosRetro( item.respuesta_promedio_min ) }}</template>
            <template v-slot:item.sin_responder="{ item }">
              <v-icon v-if="item.sin_responder" small color="red">mdi-message-alert</v-icon>
            </template>
            <template v-slot:item.no_contesto_primer="{ item }">
              <v-icon v-if="item.no_contesto_primer" small color="orange">mdi-message-off</v-icon>
            </template>
            <template v-slot:item.pdfs="{ item }">
              <span v-if="item.pdfs"><v-icon small color="blue">mdi-file-pdf-box</v-icon> {{ item.pdfs }}</span>
            </template>
            <template v-slot:item.sin_segunda_vuelta="{ item }">
              <v-icon v-if="item.sin_segunda_vuelta" small color="red">mdi-timer-alert</v-icon>
            </template>
            <template v-for="campo in camposChecklist" v-slot:[`item.${campo}`]="{ item }">
              <span :key="campo">
                <v-icon v-if="item[campo] === 1" small color="green">mdi-check-circle</v-icon>
                <v-icon v-else-if="item[campo] === 0" small color="red">mdi-close-circle</v-icon>
                <span v-else class="grey--text">—</span>
              </span>
            </template>
            <template v-slot:item.hallazgo="{ item }">
              <span class="text-caption">{{ item.hallazgo || '' }}</span>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <Alerta v-if="respuestaAlerta" :parametros="parametros"/>
  </div>
</template>

<script>
import Alerta from '@/components/alertas/Alerta.vue'
import ListaPuntosIa from '@/components/retroalimentacion/ListaPuntosIa.vue'
import validarErrores from '@/mixins/validarErrores'
import { formatoMinutosRetro, textoPeriodoHallazgos } from '@/constants/retroalimentacion-comercial'

export default {
  name: 'HallazgosTwilioDetalle',

  mixins: [ validarErrores ],

  components: {
    Alerta,
    ListaPuntosIa,
  },

  props: {
    hallazgos: { type: Object, default: null },
    idUsuario: { type: [Number, String], default: null },
    nombre: { type: String, default: '' },
    conversacionesUrl: { type: String, default: 'retroalimentacion.hallazgos.conversaciones' },
    conversacionesParams: { type: Object, default: null },
  },

  data: () => ({
    dialogConversaciones: false,
    conversaciones: [],
    cargandoConversaciones: false,
    soloAnalizadasIa: false,
    camposChecklist: [ 'identifico_necesidades', 'hubo_objecion', 'objecion_respondida', 'propuesta_valor', 'siguiente_paso' ],
    headersConversaciones: [
      { text: 'Línea'          , value: 'linea_nombre' },
      { text: 'Teléfono'       , value: 'telefono' },
      { text: 'Msgs vendedora' , value: 'mensajes_vendedora' },
      { text: 'Msgs prospecto' , value: 'mensajes_prospecto' },
      { text: 'Audios'         , value: 'audios' },
      { text: '1ª respuesta'   , value: 'primera_respuesta_min' },
      { text: 'Resp. promedio' , value: 'respuesta_promedio_min' },
      { text: 'Sin responder'  , value: 'sin_responder' },
      { text: 'No contestó 1er msg', value: 'no_contesto_primer' },
      { text: 'PDF'            , value: 'pdfs' },
      { text: 'Sin 2ª vuelta'  , value: 'sin_segunda_vuelta' },
      { text: 'Necesidades'    , value: 'identifico_necesidades' },
      { text: 'Objeción'       , value: 'hubo_objecion' },
      { text: 'Respondida'     , value: 'objecion_respondida' },
      { text: 'Valor'          , value: 'propuesta_valor' },
      { text: 'Sig. paso'      , value: 'siguiente_paso' },
      { text: 'Hallazgo IA'    , value: 'hallazgo', sortable: false, width: 320 },
    ],
  }),

  computed: {
    tieneSeguimiento () {
      return !!this.hallazgos && this.hallazgos.sin_segunda_vuelta !== null && this.hallazgos.sin_segunda_vuelta !== undefined
    },

    headersTablaConversaciones () {
      if (!this.conversaciones.some(c => c.personal)) return this.headersConversaciones
      const columnas = [ ...this.headersConversaciones ]
      columnas.splice(2, 0, { text: 'Atendió', value: 'personal' })
      return columnas
    },

    conversacionesFiltradas () {
      return this.soloAnalizadasIa
        ? this.conversaciones.filter(c => c.analizada_ia)
        : this.conversaciones
    },
  },

  methods: {
    formatoMinutosRetro,
    textoPeriodoHallazgos,

    listaHallazgos (campo) {
      return (this.hallazgos && this.hallazgos[campo]) || []
    },

    conPorcentaje (valor) {
      const total = this.hallazgos.conversaciones
      return total ? valor + ' (' + Math.round(valor * 100 / total) + '%)' : valor
    },

    verConversaciones () {
      this.conversaciones = []
      this.dialogConversaciones = true
      this.cargandoConversaciones = true
      const params = this.conversacionesParams || { id_usuario: this.idUsuario, semana_inicio: this.hallazgos.semana_inicio }
      return this.$http.get(this.conversacionesUrl, { params }).then(response => {
        this.conversaciones = response.data
      }).catch(error => {
        this.validarError(error)
      }).finally(() => { this.cargandoConversaciones = false })
    },
  },
}
</script>
